/**
 * Share-of-tokens pie for one AI-usage dimension.
 *
 * The ring is decoration: every figure it encodes is also printed in the HTML
 * legend beneath it. That is deliberate twice over — a screen reader gets real
 * text instead of an SVG, and the numbers stay assertable in jsdom, where
 * Recharts' ResponsiveContainer measures 0x0 and draws nothing at all.
 *
 * @module components/admin/AiUsagePie
 */

import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { toSlices } from './aiUsageSlices';
import type { AiUsageRow } from '../../services/aiUsageService';

interface AiUsagePieProps {
  title: string;
  rows: AiUsageRow[];
  /** Counting word for the folded bucket: "คน", "workspace". */
  unit: string;
}

export const AiUsagePie: React.FC<AiUsagePieProps> = ({ title, rows, unit }) => {
  const { slices, total } = toSlices(rows, unit);

  return (
    <div className="bg-white rounded-lg border border-neutral-200 p-4">
      <h3 className="text-sm font-semibold text-ink-900 mb-3">{title}</h3>

      {slices.length === 0 ? (
        <p className="py-8 text-center text-sm text-neutral-700">ยังไม่มีการใช้ token ในช่วงนี้</p>
      ) : (
        <>
          <div className="h-48" aria-hidden="true">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={slices} dataKey="value" nameKey="label" innerRadius="45%" outerRadius="80%">
                  {slices.map((s) => (
                    <Cell key={s.label} fill={s.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => (typeof v === 'number' ? v.toLocaleString() : String(v))} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <ul className="space-y-1.5">
            {slices.map((s) => (
              <li key={s.label} className="flex items-center gap-2 text-xs">
                <span
                  className="w-2.5 h-2.5 rounded-sm shrink-0"
                  style={{ backgroundColor: s.color }}
                  aria-hidden="true"
                />
                <span className="truncate text-ink-900" title={s.label}>
                  {s.label}
                </span>
                <span className="ml-auto shrink-0 tabular-nums text-neutral-700">
                  {s.value.toLocaleString()} ({((s.value / total) * 100).toFixed(1)}%)
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
};

export default AiUsagePie;
